import { isSupabaseConfigured, supabase } from './supabaseClient';

const defaultSettings = {
  approvalRequired: true,
  defaultPassingScore: 80,
  defaultMaxAttempts: 3,
  dueSoonDays: 3,
  sessionHours: 12,
  allowedEmailDomain: 'company.com',
  certificateIssuer: 'Internal Training Department'
};

const defaultCategories = [
  'Project Engineering',
  'Electrical Engineering',
  'Structural Engineering',
  'Design Services',
  'Permitting Services',
  'Company Process',
  'Customer Preference',
  'Tool Training'
];

export const emptyProductionState = {
  users: [],
  groups: [],
  programs: [],
  programCourses: [],
  programAssignments: [],
  courses: [],
  courseVersions: [],
  modules: [],
  lessons: [],
  quizzes: [],
  exercises: [],
  assignments: [],
  certificates: [],
  auditLogs: [],
  categories: defaultCategories,
  settings: defaultSettings
};

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeState(value) {
  return {
    ...emptyProductionState,
    ...value,
    users: safeArray(value?.users),
    groups: safeArray(value?.groups),
    programs: safeArray(value?.programs),
    programCourses: safeArray(value?.programCourses),
    programAssignments: safeArray(value?.programAssignments),
    courses: safeArray(value?.courses).map((course) => ({ ...course, trainerIds: safeArray(course.trainerIds) })),
    courseVersions: safeArray(value?.courseVersions),
    modules: safeArray(value?.modules),
    lessons: safeArray(value?.lessons),
    quizzes: safeArray(value?.quizzes),
    exercises: safeArray(value?.exercises),
    assignments: safeArray(value?.assignments).map((assignment) => ({
      ...assignment,
      quizAttempts: safeArray(assignment.quizAttempts),
      quizAnswerDrafts: assignment.quizAnswerDrafts || {},
      exerciseSubmissions: safeArray(assignment.exerciseSubmissions)
    })),
    certificates: safeArray(value?.certificates),
    auditLogs: safeArray(value?.auditLogs),
    categories: safeArray(value?.categories).length ? value.categories : defaultCategories,
    settings: { ...defaultSettings, ...(value?.settings || {}) }
  };
}

function recordError(tableName, error) {
  if (error) throw new Error(`${tableName}: ${error.message}`);
}

async function selectAll(tableName, orderColumn = null) {
  let query = supabase.from(tableName).select('*');
  if (orderColumn) query = query.order(orderColumn, { ascending: true });
  const { data, error } = await query;
  recordError(tableName, error);
  return data || [];
}

async function clearAndInsert(tableName, rows, deleteColumn = 'id') {
  const { error: deleteError } = await supabase.from(tableName).delete().neq(deleteColumn, '__never_match__');
  recordError(`${tableName} delete`, deleteError);
  if (!rows.length) return;
  const { error: insertError } = await supabase.from(tableName).insert(rows);
  recordError(`${tableName} insert`, insertError);
}

function userFromRow(row) {
  return {
    id: row.id,
    authUserId: row.auth_user_id || '',
    email: row.email,
    firstName: row.first_name || '',
    lastName: row.last_name || '',
    role: row.role,
    roles: Array.isArray(row.roles) && row.roles.length ? row.roles : [row.role || 'learner'],
    status: row.status,
    team: row.team || '',
    title: row.title || '',
    timezone: row.timezone || 'Asia/Manila',
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

function userToRow(user) {
  return {
    id: user.id,
    auth_user_id: user.authUserId || null,
    email: user.email,
    first_name: user.firstName || '',
    last_name: user.lastName || '',
    role: user.role,
    roles: Array.isArray(user.roles) && user.roles.length ? user.roles : [user.role || 'learner'],
    status: user.status || 'active',
    team: user.team || '',
    title: user.title || '',
    timezone: user.timezone || 'Asia/Manila',
    created_at: user.createdAt || new Date().toISOString(),
    updated_at: user.updatedAt || new Date().toISOString()
  };
}

function groupFromRow(row) {
  return {
    id: row.id,
    name: row.name,
    description: row.description || '',
    status: row.status || 'active',
    memberIds: row.member_ids || []
  };
}

function groupToRow(group) {
  return {
    id: group.id,
    name: group.name,
    description: group.description || '',
    status: group.status || 'active',
    member_ids: group.memberIds || []
  };
}

function courseFromRow(row) {
  return {
    id: row.id,
    title: row.title,
    description: row.description || '',
    category: row.category || '',
    targetAudience: row.target_audience || '',
    difficulty: row.difficulty || 'beginner',
    estimatedMinutes: row.estimated_minutes || 0,
    ownerId: row.owner_id,
    trainerIds: Array.isArray(row.trainer_ids) ? row.trainer_ids : [],
    status: row.status || 'draft',
    currentVersionId: row.current_version_id,
    certificateEnabled: Boolean(row.certificate_enabled),
    tags: row.tags || [],
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

function courseToRow(course) {
  return {
    id: course.id,
    title: course.title,
    description: course.description || '',
    category: course.category || '',
    target_audience: course.targetAudience || '',
    difficulty: course.difficulty || 'beginner',
    estimated_minutes: Number(course.estimatedMinutes || 0),
    owner_id: course.ownerId,
    trainer_ids: Array.isArray(course.trainerIds) ? course.trainerIds : [],
    status: course.status || 'draft',
    current_version_id: course.currentVersionId || null,
    certificate_enabled: Boolean(course.certificateEnabled),
    tags: course.tags || [],
    created_at: course.createdAt || new Date().toISOString(),
    updated_at: course.updatedAt || new Date().toISOString()
  };
}

function versionFromRow(row) {
  return {
    id: row.id,
    courseId: row.course_id,
    versionNumber: row.version_number || 1,
    status: row.status || 'draft',
    publishedAt: row.published_at,
    publishedBy: row.published_by,
    changeSummary: row.change_summary || '',
    sequential: row.sequential !== false
  };
}

function versionToRow(version) {
  return {
    id: version.id,
    course_id: version.courseId,
    version_number: Number(version.versionNumber || 1),
    status: version.status || 'draft',
    published_at: version.publishedAt || null,
    published_by: version.publishedBy || null,
    change_summary: version.changeSummary || '',
    sequential: version.sequential !== false
  };
}

function moduleFromRow(row) {
  return {
    id: row.id,
    courseVersionId: row.course_version_id,
    title: row.title,
    description: row.description || '',
    sortOrder: row.sort_order || 1,
    isRequired: row.is_required !== false
  };
}

function moduleToRow(module) {
  return {
    id: module.id,
    course_version_id: module.courseVersionId,
    title: module.title,
    description: module.description || '',
    sort_order: Number(module.sortOrder || 1),
    is_required: module.isRequired !== false
  };
}

function getPracticalSessionMeta(resources = []) {
  return safeArray(resources).find((resource) => resource.resourceType === 'practical_session') || {};
}

function stripPracticalSessionMeta(resources = []) {
  return safeArray(resources).filter((resource) => resource.resourceType !== 'practical_session');
}

function withPracticalSessionMeta(resources = [], lesson = {}) {
  const cleanResources = stripPracticalSessionMeta(resources);
  if (lesson.lessonType !== 'practical') return cleanResources;
  const sessionDate = lesson.sessionDate || '';
  const meetingLink = lesson.meetingLink || '';
  if (!sessionDate && !meetingLink) return cleanResources;
  return [
    ...cleanResources,
    {
      id: `${lesson.id || 'lesson'}-practical-session`,
      title: 'Practical activity session',
      resourceType: 'practical_session',
      sessionDate,
      meetingLink,
      sortOrder: 999
    }
  ];
}

function lessonFromRow(row) {
  const resources = row.resources || [];
  const practicalSession = getPracticalSessionMeta(resources);
  return {
    id: row.id,
    moduleId: row.module_id,
    title: row.title,
    lessonType: row.lesson_type || 'reading',
    contentHtml: row.content_html || '',
    videoUrl: row.video_url || '',
    estimatedMinutes: row.estimated_minutes || 0,
    completionType: row.completion_type || 'manual_ack',
    isRequired: row.is_required !== false,
    sortOrder: row.sort_order || 1,
    resources: stripPracticalSessionMeta(resources),
    sessionDate: row.session_date || practicalSession.sessionDate || '',
    meetingLink: row.meeting_link || practicalSession.meetingLink || ''
  };
}

function lessonToRow(lesson) {
  return {
    id: lesson.id,
    module_id: lesson.moduleId,
    title: lesson.title,
    lesson_type: lesson.lessonType || 'reading',
    content_html: lesson.contentHtml || '',
    video_url: lesson.videoUrl || '',
    estimated_minutes: Number(lesson.estimatedMinutes || 0),
    completion_type: lesson.completionType || 'manual_ack',
    is_required: lesson.isRequired !== false,
    sort_order: Number(lesson.sortOrder || 1),
    resources: withPracticalSessionMeta(lesson.resources || [], lesson)
  };
}

function quizFromRow(row) {
  return {
    id: row.id,
    courseVersionId: row.course_version_id,
    moduleId: row.module_id,
    afterLessonId: row.after_lesson_id,
    placement: row.placement || 'module_end',
    title: row.title,
    passingScore: row.passing_score || 80,
    maxAttempts: row.max_attempts || 3,
    timeLimitMinutes: row.time_limit_minutes || 15,
    questionOrder: row.question_order || 'fixed',
    showCorrectAnswers: row.show_correct_answers || 'after_attempt',
    isRequired: row.is_required !== false,
    questions: row.questions || []
  };
}

function quizToRow(quiz) {
  return {
    id: quiz.id,
    course_version_id: quiz.courseVersionId,
    module_id: quiz.moduleId || null,
    after_lesson_id: quiz.afterLessonId || null,
    placement: quiz.placement || 'module_end',
    title: quiz.title,
    passing_score: Number(quiz.passingScore || 80),
    max_attempts: Number(quiz.maxAttempts || 3),
    time_limit_minutes: Number(quiz.timeLimitMinutes || 15),
    question_order: quiz.questionOrder || 'fixed',
    show_correct_answers: quiz.showCorrectAnswers || 'after_attempt',
    is_required: quiz.isRequired !== false,
    questions: quiz.questions || []
  };
}


function exerciseFromRow(row) {
  return {
    id: row.id,
    courseVersionId: row.course_version_id,
    moduleId: row.module_id,
    title: row.title,
    instructions: row.instructions || '',
    exerciseFileTitle: row.exercise_file_title || '',
    exerciseFileUrl: row.exercise_file_url || '',
    passingScore: row.passing_score || 80,
    maxScore: row.max_score || 100,
    isRequired: row.is_required !== false,
    sortOrder: row.sort_order || 1
  };
}

function exerciseToRow(exercise) {
  return {
    id: exercise.id,
    course_version_id: exercise.courseVersionId,
    module_id: exercise.moduleId || null,
    title: exercise.title,
    instructions: exercise.instructions || '',
    exercise_file_title: exercise.exerciseFileTitle || 'Exercise file',
    exercise_file_url: exercise.exerciseFileUrl || '',
    passing_score: Number(exercise.passingScore || 80),
    max_score: Number(exercise.maxScore || 100),
    is_required: exercise.isRequired !== false,
    sort_order: Number(exercise.sortOrder || 1)
  };
}


function programFromRow(row) {
  return {
    id: row.id,
    title: row.title,
    description: row.description || '',
    category: row.category || '',
    status: row.status || 'draft',
    createdBy: row.created_by || '',
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

function programToRow(program) {
  return {
    id: program.id,
    title: program.title,
    description: program.description || '',
    category: program.category || '',
    status: program.status || 'draft',
    created_by: program.createdBy || null,
    created_at: program.createdAt || new Date().toISOString(),
    updated_at: program.updatedAt || new Date().toISOString()
  };
}

function programCourseFromRow(row) {
  return {
    id: row.id,
    programId: row.program_id,
    courseId: row.course_id,
    sortOrder: row.sort_order || 1,
    createdAt: row.created_at
  };
}

function programCourseToRow(item) {
  return {
    id: item.id,
    program_id: item.programId,
    course_id: item.courseId,
    sort_order: Number(item.sortOrder || 1),
    created_at: item.createdAt || new Date().toISOString()
  };
}

function programAssignmentFromRow(row) {
  return {
    id: row.id,
    programId: row.program_id,
    assignedToUserId: row.assigned_to_user_id,
    assignedByUserId: row.assigned_by_user_id,
    assignedToGroupId: row.assigned_to_group_id || '',
    assignmentSource: row.assignment_source || 'direct',
    dueDate: row.due_date || '',
    required: row.required !== false,
    status: row.status || 'not_started',
    assignedAt: row.assigned_at,
    completedAt: row.completed_at
  };
}

function programAssignmentToRow(assignment) {
  return {
    id: assignment.id,
    program_id: assignment.programId,
    assigned_to_user_id: assignment.assignedToUserId,
    assigned_by_user_id: assignment.assignedByUserId,
    assigned_to_group_id: assignment.assignedToGroupId || null,
    assignment_source: assignment.assignmentSource || 'direct',
    due_date: assignment.dueDate || null,
    required: assignment.required !== false,
    status: assignment.status || 'not_started',
    assigned_at: assignment.assignedAt || new Date().toISOString(),
    completed_at: assignment.completedAt || null
  };
}

function assignmentFromRow(row) {
  return {
    id: row.id,
    courseId: row.course_id,
    courseVersionId: row.course_version_id,
    assignedToUserId: row.assigned_to_user_id,
    assignedByUserId: row.assigned_by_user_id,
    programId: row.program_id || '',
    programAssignmentId: row.program_assignment_id || '',
    assignmentSource: row.assignment_source || 'direct',
    dueDate: row.due_date || '',
    required: row.required !== false,
    status: row.status || 'not_started',
    startedAt: row.started_at,
    completedAt: row.completed_at,
    lessonProgress: row.lesson_progress || {},
    quizAttempts: row.quiz_attempts || [],
    quizAnswerDrafts: row.quiz_answer_drafts || {},
    exerciseSubmissions: row.exercise_submissions || []
  };
}

function assignmentToRow(assignment) {
  return {
    id: assignment.id,
    course_id: assignment.courseId,
    course_version_id: assignment.courseVersionId,
    assigned_to_user_id: assignment.assignedToUserId,
    assigned_by_user_id: assignment.assignedByUserId,
    program_id: assignment.programId || null,
    program_assignment_id: assignment.programAssignmentId || null,
    assignment_source: assignment.assignmentSource || 'direct',
    due_date: assignment.dueDate || null,
    required: assignment.required !== false,
    status: assignment.status || 'not_started',
    started_at: assignment.startedAt || null,
    completed_at: assignment.completedAt || null,
    lesson_progress: assignment.lessonProgress || {},
    quiz_attempts: assignment.quizAttempts || [],
    quiz_answer_drafts: assignment.quizAnswerDrafts || {},
    exercise_submissions: assignment.exerciseSubmissions || []
  };
}

function certificateFromRow(row) {
  return {
    id: row.id,
    assignmentId: row.assignment_id,
    userId: row.user_id,
    courseId: row.course_id,
    courseVersionId: row.course_version_id,
    certificateNumber: row.certificate_number,
    verificationCode: row.verification_code,
    issuedAt: row.issued_at,
    expiresAt: row.expires_at,
    status: row.status || 'active'
  };
}

function certificateToRow(certificate) {
  return {
    id: certificate.id,
    assignment_id: certificate.assignmentId,
    user_id: certificate.userId,
    course_id: certificate.courseId,
    course_version_id: certificate.courseVersionId,
    certificate_number: certificate.certificateNumber,
    verification_code: certificate.verificationCode,
    issued_at: certificate.issuedAt || new Date().toISOString(),
    expires_at: certificate.expiresAt || null,
    status: certificate.status || 'active'
  };
}

function auditFromRow(row) {
  return {
    id: row.id,
    actorUserId: row.actor_user_id,
    action: row.action,
    entityType: row.entity_type,
    entityId: row.entity_id,
    createdAt: row.created_at
  };
}

function auditToRow(log) {
  return {
    id: log.id,
    actor_user_id: log.actorUserId,
    action: log.action,
    entity_type: log.entityType,
    entity_id: log.entityId,
    created_at: log.createdAt || new Date().toISOString()
  };
}


export async function ensureLmsProfileForAuthUser(authUser) {
  if (!isSupabaseConfigured || !supabase || !authUser?.id) {
    return { ok: false, user: null, message: 'Supabase Auth session is required.' };
  }

  const email = authUser.email?.trim().toLowerCase() || '';
  try {
    let { data: profile, error } = await supabase
      .from('lms_users')
      .select('*')
      .eq('auth_user_id', authUser.id)
      .maybeSingle();
    recordError('lms_users auth profile lookup', error);

    if (!profile && email) {
      const fallback = await supabase
        .from('lms_users')
        .select('*')
        .eq('email', email)
        .maybeSingle();
      recordError('lms_users email profile lookup', fallback.error);
      profile = fallback.data;

      if (profile && !profile.auth_user_id) {
        const update = await supabase
          .from('lms_users')
          .update({ auth_user_id: authUser.id, updated_at: new Date().toISOString() })
          .eq('id', profile.id)
          .select('*')
          .single();
        recordError('lms_users auth profile link', update.error);
        profile = update.data;
      }
    }

    if (!profile) {
      const metadata = authUser.user_metadata || {};
      const fullName = metadata.full_name || metadata.name || '';
      const [firstName, ...rest] = fullName.split(' ').filter(Boolean);
      const created = {
        id: authUser.id,
        auth_user_id: authUser.id,
        email,
        first_name: metadata.first_name || firstName || '',
        last_name: metadata.last_name || rest.join(' ') || '',
        role: 'learner',
        roles: ['learner'],
        status: 'active',
        team: metadata.team || 'Unassigned',
        title: metadata.title || 'Learner',
        timezone: metadata.timezone || 'Asia/Manila',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      const inserted = await supabase.from('lms_users').insert(created).select('*').single();
      recordError('lms_users auth profile create', inserted.error);
      profile = inserted.data;
    }

    return { ok: true, user: userFromRow(profile), message: 'Supabase Auth profile ready.' };
  } catch (error) {
    return { ok: false, user: null, message: error.message };
  }
}

export async function createAuthUserWithProfile(input) {
  if (!isSupabaseConfigured || !supabase) {
    return { ok: false, user: null, message: 'Supabase environment variables are required.' };
  }

  const { data, error } = await supabase.functions.invoke('admin-create-user', {
    body: {
      email: input.email,
      password: input.password,
      firstName: input.firstName,
      lastName: input.lastName,
      role: input.role,
      roles: input.roles,
      status: input.status || 'active',
      team: input.team || 'Unassigned',
      title: input.title || '',
      timezone: input.timezone || 'Asia/Manila'
    }
  });

  if (error) {
    return { ok: false, user: null, message: error.message || 'Supabase Edge Function admin-create-user failed.' };
  }
  if (!data?.ok) {
    return { ok: false, user: null, message: data?.message || 'User was not created.' };
  }

  return { ok: true, user: data.user, message: data.message || 'User created.' };
}

export async function bulkCreateAuthUsersWithProfiles(rows) {
  if (!isSupabaseConfigured || !supabase) {
    return { ok: false, users: [], results: [], summary: null, message: 'Supabase environment variables are required.' };
  }

  const { data, error } = await supabase.functions.invoke('admin-bulk-create-users', {
    body: { rows }
  });

  if (error) {
    return { ok: false, users: [], results: [], summary: null, message: error.message || 'Supabase Edge Function admin-bulk-create-users failed.' };
  }
  if (!data?.ok) {
    return { ok: false, users: data?.users || [], results: data?.results || [], summary: data?.summary || null, message: data?.message || 'Bulk users were not imported.' };
  }

  return {
    ok: true,
    users: data.users || [],
    results: data.results || [],
    summary: data.summary || null,
    message: data.message || 'Bulk user import completed.'
  };
}

export async function loadLmsStateFromSupabase() {
  if (!isSupabaseConfigured || !supabase) {
    return { ok: false, configured: false, data: null, message: 'Supabase environment variables are required for this production build.' };
  }

  try {
    const [
      users,
      groups,
      programs,
      programCourses,
      programAssignments,
      courses,
      versions,
      modules,
      lessons,
      quizzes,
      exercises,
      assignments,
      certificates,
      auditLogs,
      categories,
      settingsRows
    ] = await Promise.all([
      selectAll('lms_users', 'email'),
      selectAll('lms_groups', 'name'),
      selectAll('lms_programs', 'created_at'),
      selectAll('lms_program_courses', 'sort_order'),
      selectAll('lms_program_assignments', 'assigned_at'),
      selectAll('lms_courses', 'created_at'),
      selectAll('lms_course_versions', 'version_number'),
      selectAll('lms_modules', 'sort_order'),
      selectAll('lms_lessons', 'sort_order'),
      selectAll('lms_quizzes', 'title'),
      selectAll('lms_exercises', 'sort_order'),
      selectAll('lms_assignments', 'id'),
      selectAll('lms_certificates', 'issued_at'),
      selectAll('lms_audit_logs', 'created_at'),
      selectAll('lms_categories', 'name'),
      selectAll('lms_settings', 'id')
    ]);

    const loaded = normalizeState({
      users: users.map(userFromRow),
      groups: groups.map(groupFromRow),
      programs: programs.map(programFromRow),
      programCourses: programCourses.map(programCourseFromRow),
      programAssignments: programAssignments.map(programAssignmentFromRow),
      courses: courses.map(courseFromRow),
      courseVersions: versions.map(versionFromRow),
      modules: modules.map(moduleFromRow),
      lessons: lessons.map(lessonFromRow),
      quizzes: quizzes.map(quizFromRow),
      exercises: exercises.map(exerciseFromRow),
      assignments: assignments.map(assignmentFromRow),
      certificates: certificates.map(certificateFromRow),
      auditLogs: auditLogs.map(auditFromRow).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
      categories: categories.map((row) => row.name),
      settings: settingsRows.find((row) => row.id === 'global')?.data || defaultSettings
    });

    return { ok: true, configured: true, data: loaded, message: 'Supabase production tables loaded.' };
  } catch (error) {
    return { ok: false, configured: true, data: null, message: error.message };
  }
}


export async function saveLearningMutationToSupabase(nextState, assignmentId, auditLogs = []) {
  if (!isSupabaseConfigured || !supabase) {
    return { ok: false, configured: false, message: 'Supabase environment variables are required for this production build.' };
  }

  const state = normalizeState(nextState);
  const assignment = state.assignments.find((item) => item.id === assignmentId);
  if (!assignment) {
    return { ok: false, configured: true, message: 'Assignment not found while saving learner progress.' };
  }

  try {
    const { error: assignmentError } = await supabase
      .from('lms_assignments')
      .upsert(assignmentToRow(assignment), { onConflict: 'id' });
    recordError('lms_assignments progress upsert', assignmentError);

    if (assignment.programAssignmentId) {
      const programAssignment = state.programAssignments.find((item) => item.id === assignment.programAssignmentId);
      if (programAssignment) {
        const { error: programAssignmentError } = await supabase
          .from('lms_program_assignments')
          .upsert(programAssignmentToRow(programAssignment), { onConflict: 'id' });
        recordError('lms_program_assignments progress upsert', programAssignmentError);
      }
    }

    const assignmentCertificates = state.certificates.filter((certificate) => certificate.assignmentId === assignmentId);
    if (assignmentCertificates.length) {
      const { error: certificateError } = await supabase
        .from('lms_certificates')
        .upsert(assignmentCertificates.map(certificateToRow), { onConflict: 'id' });
      recordError('lms_certificates progress upsert', certificateError);
    }

    if (auditLogs.length) {
      const { error: auditError } = await supabase
        .from('lms_audit_logs')
        .upsert(auditLogs.map(auditToRow), { onConflict: 'id' });
      recordError('lms_audit_logs progress upsert', auditError);
    }

    return { ok: true, configured: true, message: 'Learner progress saved to Supabase.' };
  } catch (error) {
    return { ok: false, configured: true, message: error.message };
  }
}

export async function saveLmsStateToSupabase(nextState) {
  if (!isSupabaseConfigured || !supabase) {
    return { ok: false, configured: false, message: 'Supabase environment variables are required for this production build.' };
  }

  const state = normalizeState(nextState);
  try {
    await clearAndInsert('lms_categories', state.categories.map((name) => ({ name })), 'name');
    await clearAndInsert('lms_settings', [{ id: 'global', data: state.settings, updated_at: new Date().toISOString() }]);
    await clearAndInsert('lms_users', state.users.map(userToRow));
    await clearAndInsert('lms_groups', state.groups.map(groupToRow));
    await clearAndInsert('lms_programs', state.programs.map(programToRow));
    await clearAndInsert('lms_program_courses', state.programCourses.map(programCourseToRow));
    await clearAndInsert('lms_program_assignments', state.programAssignments.map(programAssignmentToRow));
    await clearAndInsert('lms_courses', state.courses.map(courseToRow));
    await clearAndInsert('lms_course_versions', state.courseVersions.map(versionToRow));
    await clearAndInsert('lms_modules', state.modules.map(moduleToRow));
    await clearAndInsert('lms_lessons', state.lessons.map(lessonToRow));
    await clearAndInsert('lms_quizzes', state.quizzes.map(quizToRow));
    await clearAndInsert('lms_exercises', state.exercises.map(exerciseToRow));
    await clearAndInsert('lms_assignments', state.assignments.map(assignmentToRow));
    await clearAndInsert('lms_certificates', state.certificates.map(certificateToRow));
    await clearAndInsert('lms_audit_logs', state.auditLogs.map(auditToRow));
    return { ok: true, configured: true, message: 'Saved to Supabase production tables.' };
  } catch (error) {
    return { ok: false, configured: true, message: error.message };
  }
}

export async function testSupabaseConnection() {
  if (!isSupabaseConfigured || !supabase) {
    return { ok: false, configured: false, message: 'Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY.' };
  }

  const { error } = await supabase.from('lms_users').select('id').limit(1);
  if (error) return { ok: false, configured: true, message: error.message };
  return { ok: true, configured: true, message: 'Supabase production tables are reachable.' };
}
